import angular, { IComponentOptions, IRootScopeService, IScope } from 'angular';
import { StateParams, StateService } from 'angular-ui-router';
import { ngStorage } from 'ngstorage';
import {
    IFontFamilyModel,
    IFontStyleModel,
    Paginator,
    SortOption,
} from '../../tn.common';
import { pangramService } from '../../tn.fonts/pangram.service/pangram';
import { PopoverService } from '../../tn.popover/service.tn.popover';
import template from './tnViewToolbar2.html';

export interface IViewToolbarSortOptions {
    name?: SortOption;
    released?: SortOption;
    size?: SortOption;
    weight?: SortOption;
    width?: SortOption;
}

export class TnViewToolbarController2 {
    // bindings
    count?: number;
    countTotal?: number;
    // ng-pluralize arguments
    countLabel?: { one: string; other: string };
    disableSizeSort = false;
    disableWeightSort = false;
    disableWidthSort = false;

    pagination?: Paginator;
    showTrial = false;
    sort: keyof IViewToolbarSortOptions = 'name';
    sortOptions: IViewToolbarSortOptions = {};
    // this is only used for suggested use: min_recommended_sze-max_recommended size which is put in place of the counts
    // on views with single families. TODO: make a transclusion spot which callers can provide a SuggestedUseComponent or CountsComponent
    // to simplify the internal logic of this component.
    style?: IFontStyleModel;
    // these are passed to the buy/save buttons and uses to set the sort options
    // generally this should be the filtered styles if coming from a filtered view.
    styles: IFontStyleModel[] = [];
    trialFamily?: IFontFamilyModel;

    // locals
    customFilter = '';
    customText = '';
    customTextChanged = false;
    customTextOptions: string[] = [];
    customTextIsFocused = false;
    customTextMouseover = false;
    size?;

    // Popovers
    sharePopover = this.$tnPopover.popoverXS(
        this.$element.find('.popover_share')
    );
    sizeSliderPopover = this.$tnPopover.popoverXS(
        this.$element.find('.popover_size_slider')
    );
    sortPopover = this.$tnPopover.popoverXS(
        this.$element.find('.popover_sort')
    );
    buySelectedPopover = this.$tnPopover.popoverXS(
        this.$element.find('.popover_tooltip__buySelected')
    );

    static $inject = [
        '$scope',
        '$rootScope',
        'pangramService',
        '$localStorage',
        '$stateParams',
        '$tnPopover',
        '$element',
        '$state',
    ];

    constructor(
        protected $scope: IScope,
        protected $rootScope: IRootScopeService,
        protected pangramService: pangramService,
        protected $localStorage: ngStorage.StorageService,
        protected $stateParams: StateParams,
        protected $tnPopover: PopoverService,
        protected $element: JQuery,
        protected $state: StateService
    ) {
        pangramService.list().then((pangrams) => {
            this.customTextOptions = pangrams;
        });
        this.customText = $localStorage.specimenText || '';

        // this prevents the popover from reappearing when tabbing
        // back if you left while the custom text input field was focused
        angular.element(window).on('blur', () => this.blurCustomText());
        $scope.$on('$destroy', () =>
            angular.element(window).off('blur', () => this.blurCustomText())
        );
        this.updateDisabledSorts();
    }

    // $onChanges(changes: Record<string, { currentValue: unknown }>): void {
    //     console.log('TnViewToolbarController2.$onChanges', { changes });
    // }

    updateDisabledSorts(): void {
        const weights = this.styles.map((style) => style.weight);
        const uniqueWeights = Array.from(new Set(weights));
        this.disableWeightSort = uniqueWeights.length <= 1;

        const widths = this.styles.map((style) => style.width);
        const uniqueWidths = Array.from(new Set(widths));
        this.disableWidthSort = uniqueWidths.length <= 1;
    }

    clearCustomText(): void {
        this.$localStorage.specimenText = '';
        this.customText = '';
    }

    focusCustomText(): void {
        angular.element('#viewtoolbar_customtext').focus();
    }

    blurCustomText(): void {
        angular.element('#viewtoolbar_customtext').blur();
    }

    clearCustomFilter(): void {
        this.customFilter = '';
    }

    nextPage(): void {
        this.pagination?.nextPage();
    }

    prevPage(): void {
        this.pagination?.prevPage();
    }

    setSortWithURI(newSort: keyof IViewToolbarSortOptions): void {
        const option = this.sortOptions[newSort];
        // const set to invalid sort.
        if (!option) return;
        if (this.sort === newSort) {
            option.asc = !option.asc;
        } else {
            this.sort = newSort;
        }
        this.$state.go('.', { sort: option.value() });
        this.sortPopover.hide();
    }

    setCustomText(text: string): void {
        this.customText = text;
        this.$localStorage.specimenText = text;
    }

    onPangramSelected(pangram: string): void {
        this.setCustomText(pangram);
        this.customTextMouseover = false;
        this.blurCustomText();
    }

    onCustomTextTyped(): void {
        this.setCustomText(this.customText);
        this.customTextChanged = true;
    }

    showSharePopover($event: Event): void {
        this.sharePopover.show($event.currentTarget);
    }
}

export const TnViewToolbarComponent: IComponentOptions = {
    bindings: {
        count: '<?',
        countTotal: '<?',
        countLabel: '<?',
        customText: '=',
        customFilter: '=?',
        disableSizeSort: '<',
        disableWeightSort: '<',
        disableWidthSort: '<',
        onCustomChanged: '&?',
        pagination: '<?',
        showBuy: '<',
        showCount: '<?',
        showSave: '<',
        // trial family must also be set for the trial button to show
        showTrial: '<',
        size: '=?',
        sizeMax: '=',
        sizeMin: '=',
        sizeStep: '=',
        sort: '<?',
        sortOptions: '<?',
        style: '<?',
        styles: '<?',
        trialFamily: '<?',
    },
    controller: TnViewToolbarController2,
    controllerAs: 'vm',
    template: template,
};
